import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Send, Calendar, MapPin, Users, X, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { useInviteCart } from './InviteCartProvider';
import { formatTime12h } from '../utils/timeFormat';
import MarketBadge from './MarketBadge';

/**
 * Iter-187 — Cart Review + Send drawer.
 *
 * Lists every chip currently in the global Invite Cart, lets admin
 * remove individual entries before send, pick an open shift in the
 * next 14 days, choose channels (email / in-app / SMS), add a custom
 * message, and dispatch the batch invite. Each shift row in the picker
 * surfaces booked-count + spots-remaining + role + venue + date/time.
 *
 * Posts to POST /admin/shifts/{shift_id}/invite-talent with
 *   { invite_all: false, target_user_ids, send_email, send_in_app,
 *     send_sms, custom_message }
 * — same endpoint used by single-talent invites, so URL helpers, audit
 * logging, and retry-failed flow stay identical.
 */
const InviteCartDrawer = () => {
  const { items, remove, clear, isReviewOpen, closeReview, preselectedShift } = useInviteCart();
  const [shifts, setShifts] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [sendInApp, setSendInApp] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isReviewOpen) return;
    let cancelled = false;
    setLoadingShifts(true);
    // Iter-193 — if the cart was opened from a Shift Detail page, lock
    // the selection to that *live* shift before the picker even loads.
    // We still fetch the list so the admin can switch, but the default
    // is no longer "first random open shift" (which used to surface
    // archived fake Miami seeds).
    if (preselectedShift?.id) {
      setSelectedShiftId(String(preselectedShift.id));
    }
    api.get('/admin/shifts/open-next-14-days')
      .then((res) => {
        if (cancelled) return;
        let rows = Array.isArray(res.data) ? res.data : [];
        // Make sure the preselected shift appears at the top of the
        // picker even if it isn't in the next-14-days window or was
        // filtered out (e.g. it's a single-spot open shift the admin
        // explicitly launched from).
        if (preselectedShift?.id) {
          const exists = rows.some((r) => String(r.id) === String(preselectedShift.id));
          if (!exists) rows = [preselectedShift, ...rows];
        }
        setShifts(rows);
        if (preselectedShift?.id) {
          setSelectedShiftId(String(preselectedShift.id));
        } else if (!selectedShiftId && rows[0]) {
          setSelectedShiftId(rows[0].id);
        }
      })
      .catch(() => { if (!cancelled) toast.error('Could not load open shifts'); })
      .finally(() => { if (!cancelled) setLoadingShifts(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewOpen, preselectedShift]);

  const selectedShift = useMemo(
    () => shifts.find((s) => s.id === selectedShiftId),
    [shifts, selectedShiftId],
  );

  const submit = async () => {
    if (!selectedShiftId || items.length === 0) return;
    if (!sendEmail && !sendInApp && !sendSms) {
      toast.error('Pick at least one channel');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(
        `/admin/shifts/${selectedShiftId}/invite-talent`,
        {
          invite_all: false,
          target_user_ids: items.map((i) => i.user_id),
          send_email: sendEmail,
          send_in_app: sendInApp,
          send_sms: sendSms,
          custom_message: customMessage.trim() || null,
        },
      );
      const r = res.data || {};
      if (r.queued) {
        toast.success(`Invites queued for ${r.queued_count || items.length} talent — sending in the background.`);
        clear();
        closeReview();
        return;
      }
      const s = r.sent || {};
      const f = r.failed || {};
      const totalSent = (s.email || 0) + (s.in_app || 0) + (s.sms || 0);
      const totalFailed = (f.email || 0) + (f.in_app || 0) + (f.sms || 0);
      if (totalSent === 0 && totalFailed > 0) {
        const reason = (r.failure_samples || [])[0]?.reason || 'send failed';
        toast.error(`Invite failed — ${reason}`, { duration: 7000 });
      } else if (totalFailed > 0) {
        toast.warning(
          `Sent to ${r.recipients_matched || items.length} — ${s.email || 0}E · ${s.in_app || 0}I · ${s.sms || 0}S · ${totalFailed} failed`,
          { duration: 6000 },
        );
        clear();
        closeReview();
      } else {
        toast.success(
          `Invited ${r.recipients_matched || items.length} talent — ${s.email || 0}E · ${s.in_app || 0}I · ${s.sms || 0}S`,
          { duration: 4000 },
        );
        clear();
        closeReview();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSubmitting(false);
    }
  };

  // Iter-192 — selected shift's market_id, used to highlight in-market chips.
  const highlightMarketIds = useMemo(() => {
    const s = shifts.find((x) => x.id === selectedShiftId);
    return s && s.market_id ? [s.market_id] : null;
  }, [shifts, selectedShiftId]);

  return (
    <Dialog open={isReviewOpen} onOpenChange={(o) => { if (!o) closeReview(); }}>
      <DialogContent
        className="max-w-lg max-h-[92vh] overflow-y-auto"
        data-testid="invite-cart-review-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListPlus className="w-5 h-5 text-cyan-600" />
            Invite List · {items.length} selected
          </DialogTitle>
          <DialogDescription>
            Review the talent you've added, pick an open gig, and send a one-tap invite.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Selected talent chips */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Selected talent</Label>
            {items.length === 0 ? (
              <div className="text-sm text-gray-500 border rounded-md p-3 bg-gray-50" data-testid="cart-empty-state">
                Your Invite List is empty. Add talent from Discover, Favorites, or any admin list.
              </div>
            ) : (
              <div
                className="max-h-44 overflow-y-auto flex flex-wrap gap-1.5 border rounded-md p-2 bg-gray-50"
                data-testid="cart-chip-list"
              >
                {items.map((t) => (
                  <div
                    key={t.user_id}
                    className="bg-white border border-gray-200 rounded-md px-2 py-1 flex items-center gap-2 text-xs"
                    data-testid={`cart-chip-${t.user_id}`}
                  >
                    {t.photo_url && (
                      <img src={t.photo_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                    )}
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="truncate max-w-[160px] text-gray-900 font-medium">{t.name}</span>
                      {(t.markets?.length > 0 || t.primary_market) && (
                        <MarketBadge
                          markets={t.markets || []}
                          primaryMarket={t.primary_market}
                          highlightMarketIds={highlightMarketIds}
                          testId={`cart-chip-market-${t.user_id}`}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(t.user_id)}
                      className="ml-0.5 text-gray-400 hover:text-rose-500 rounded-full"
                      data-testid={`cart-chip-remove-${t.user_id}`}
                      aria-label={`Remove ${t.name} from Invite List`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Gig picker */}
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Pick an open gig (next 14 days)</Label>
            {loadingShifts ? (
              <div className="text-sm text-gray-500 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : shifts.length === 0 ? (
              <div className="text-sm text-gray-500 border rounded-md p-3 bg-gray-50" data-testid="cart-no-open-shifts">
                No open shifts in the next 14 days.
              </div>
            ) : (
              <div className="max-h-56 overflow-y-auto space-y-1 border rounded-md p-1" data-testid="cart-shift-picker">
                {shifts.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSelectedShiftId(s.id)}
                    className={`w-full text-left p-2 rounded transition-colors ${
                      selectedShiftId === s.id ? 'bg-cyan-50 border border-cyan-300' : 'hover:bg-gray-50 border border-transparent'
                    }`}
                    data-testid={`cart-shift-option-${s.id}`}
                  >
                    <p className="text-sm font-semibold text-gray-900 truncate">{s.event_title}</p>
                    <p className="text-[11px] text-gray-600 truncate">{s.role_name}{s.venue_name ? ` · ${s.venue_name}` : ''}</p>
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                      <span className="flex items-center gap-0.5">
                        <Calendar className="w-3 h-3" /> {s.shift_date} · {formatTime12h(s.start_time)}
                      </span>
                      <span className="flex items-center gap-0.5 truncate">
                        <MapPin className="w-3 h-3" /> {s.city}, {s.state}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <Users className="w-3 h-3" /> {(s.talent_needed || 0) - (s.spots_remaining || 0)} booked · {s.spots_remaining}/{s.talent_needed} open
                      </span>
                      {s.market_metro_name && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-medium" data-testid={`cart-shift-market-${s.market_slug || 'unknown'}`}>
                          <MapPin className="w-3 h-3" />
                          {(s.market_metro_name || '').split(',')[0].split('/')[0].trim()} Market
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Channels */}
          <div className="space-y-2 border-t pt-3">
            <Label className="text-xs uppercase tracking-wide text-gray-500">Channels</Label>
            <div className="space-y-2">
              {[
                { key: 'email', state: sendEmail, set: setSendEmail, label: 'Email' },
                { key: 'in_app', state: sendInApp, set: setSendInApp, label: 'In-app notification' },
                { key: 'sms', state: sendSms, set: setSendSms, label: 'SMS' },
              ].map((c) => (
                <div key={c.key} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{c.label}</span>
                  <Switch
                    checked={c.state}
                    onCheckedChange={c.set}
                    data-testid={`cart-channel-${c.key}-toggle`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Custom message */}
          <div className="space-y-2 border-t pt-3">
            <Label htmlFor="cart-invite-message" className="text-xs uppercase tracking-wide text-gray-500">
              Custom message (optional)
            </Label>
            <Textarea
              id="cart-invite-message"
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Add a quick personal note for everyone in this batch…"
              rows={3}
              maxLength={500}
              data-testid="cart-invite-custom-message"
            />
          </div>

          {/* Selected gig summary */}
          {selectedShift && (
            <div className="text-xs text-gray-600 border-t pt-3" data-testid="cart-selected-gig-summary">
              Sending to <strong className="text-gray-900">{items.length}</strong> talent for
              <strong className="text-gray-900"> {selectedShift.event_title}</strong>
              {' '}({selectedShift.role_name} · {selectedShift.shift_date}).
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => clear()} disabled={items.length === 0} data-testid="cart-clear-btn">
            Clear list
          </Button>
          <Button variant="outline" onClick={closeReview}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting || !selectedShiftId || items.length === 0}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="cart-send-invites-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : (
              <Send className="w-4 h-4 mr-1" />
            )}
            Invite Selected Talent ({items.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteCartDrawer;
